<template>
  <div
    v-click-outside="hideDropdownOptions"
    class="sb-dropdown"
    data-sb-regression="sb-dropdown"
    :class="{ active: showDropdown, disabled }"
  >
    <div
      ref="selection"
      class="selection-wrapper"
      @click="toggleDropdownOptions"
    >
      <div
        v-if="noSelection"
        class="placeholder"
      >
        <sb-text :value="placeholder" />
      </div>
      <!-- @slot selected value slot -->
      <slot
        v-else-if="hasSelectionScopedSlot"
        name="selection"
        :selected="value"
      />
      <SbSelectedOption v-else>
        {{ value }}
      </SbSelectedOption>
      <i
        aria-hidden="true"
        class="sb-icon-caret-down"
        :class="{ 'expanded': showDropdown }"
      />
    </div>
    <SbPopover
      :target="popoverTarget"
      :active="showDropdown"
      placement="start"
      orientation="bottom"
      :boundaries-element="boundariesElement"
      :position-fixed="positionFixed"
    >
      <FadeTransition :duration="fadeDuration">
        <SbLoadingPromiseWrapper
          v-show="showDropdown"
          ref="sb-options-popover"
          v-model:loading="isLoading"
          size="xtsm"
          :loading-promise="loadingPromise"
          :classes="loadingClasses"
        >
          <!-- @slot slot to render empty options text -->
          <slot
            v-if="!hasSelectionOptions"
            name="empty-options"
          >
            <SbEmptyOptions>
              <sb-text :value="emptyOptionsText" />
            </SbEmptyOptions>
          </slot>
          <!-- @slot slot to render empty options -->
          <slot
            v-if="!hasOptionsDefaultSlot"
            :options="optionsInternal"
          />
          <slot v-else />
        </SbLoadingPromiseWrapper>
      </FadeTransition>
    </SbPopover>
  </div>
</template>

<script>
import fieldHelper from '@src/utils/mixins/fieldHelperMixin';
import labelHelper from '@src/utils/mixins/labelHelperMixin';
import DeprecationTrackingMixin from '@src/utils/mixins/deprecatedComponentTracking';
import FieldStateEnum from '@src/utils/enums/fieldStateEnum';
import SbText from '@src/components/Text/SbText.vue';

import isEqual from 'lodash/isEqual';
import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';
import EscapeKeyMixin from '@src/utils/mixins/escapeKey';

import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbEmptyOptions from '@src/components/Dropdown/components/SbEmptyOptions.vue';
import SbSelectedOption from '@src/components/Dropdown/components/SbSelectedOption.vue';

/**
 * Dropdowns allows users to select between multiple options.
 * It also displays the current selected option(s)
 * the selection can be bound in your parent component using the `v-model` syntax.
 * If the bound model is of type `Array`, you will be able to make multiple selections.
 * Otherwise the selection will be a single element.
 */
export default {
  name: 'SbDropdown',
  components: {
    SbSelectedOption,
    SbEmptyOptions,
    SbPopover,
    FadeTransition,
    SbLoadingPromiseWrapper,
    SbText,
  },
  mixins: [EscapeKeyMixin, ClickOutsideDirectiveMixin, labelHelper, fieldHelper, DeprecationTrackingMixin],
  provide() {
    const { SbSelect } = this;
    return { SbSelect };
  },
  props: {
    /**
     * Number of options to be displayed on expanding options panel
     */
    resultsLimit: {
      type: Number,
      default: 2,
    },
    /**
     * Text to be displayed as the current value of the dropdown
     */
    value: {}, // eslint-disable-line
    /**
     * Text to be displayed if the current value of the dropdown is falsey.
     */
    placeholder: {
      type: String,
      default: 'common.selectOption',
    },
    /**
     * List of options to populate the dropdown.
     * If you provide this prop then the dropdown will do the list iteration for you
     * and expose the option as part of a scoped slot.
     */
    options: {
      type: Array,
      required: false,
      default: null,
    },
    /**
     * This is the text to be used if the options list is empty.
     * If you need control over the markup of the empty options, you can use the `empty-options` slot.
     * When using this, it will override the text value with that markup. Please use the `sb-empty-options` component to get some default styling.
     */
    emptyOptionsText: {
      type: String,
      default: 'sbDropdown.noOptions',
    },
    /**
     * A promise which will be used to apply loading styles until its resolved/rejected
    */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
     * Disable dropdown from user input
    */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Bounding context to fire the intersection callback on the container.
     * Must of type Element.
     */
    boundariesElement: {
      type: [String, HTMLElement],
      default: 'viewport',
    },
    /**
     * Whether we want to make the popover sticky
     */
    positionFixed: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Emitted when the selected value is changed
     */
    'update:value',
    'set-label-for',
  ],
  data: () => ({
    showDropdown: false,
    isLoading: null,
    SbSelect: {
      options: [],
      selected: null,
      setSelected: null,
      hasProvidedOptions: false,
    },
    popoverTarget: null,
  }),
  computed: {
    loadingClasses() {
      const classes = ['options-popover'];
      if (this.isLoading) classes.push('options-loading');
      if (this.positionFixed) classes.push('options-popover--fixed');
      return classes;
    },
    noSelection() {
      if (this.selectedIsArray) return this.selected.length === 0;
      return !this.selected;
    },
    selectedIsArray() {
      return Array.isArray(this.selected);
    },
    fadeDuration() {
      return 100;
    },
    hasOptionsDefaultSlot() {
      return this.$slots.default && this.$slots.default();
    },
    hasSelectionScopedSlot() {
      return this.$slots.selection && this.$slots.selection();
    },
    optionsInternal() {
      return this.SbSelect.options;
    },
    hasSelectionOptions() {
      return this.optionsInternal.length > 0;
    },
    selected: {
      get() {
        return this.SbSelect.selected;
      },
      set(nv) {
        this.SbSelect.selected = nv;
      },
    },
  },
  watch: {
    options: {
      immediate: true,
      handler(newOptions) {
        this.SbSelect.options = newOptions || this.SbSelect.options;
        this.calculateHeight();
      },
    },
    value: {
      handler(newVal) {
        if (!isEqual(newVal, this.SbSelect.selected)) {
          this.SbSelect.selected = newVal;
        }
      },
    },
    showDropdown(show) {
      if (show) {
        this.calculateHeight();
      }
    },
  },
  mounted() {
    this.popoverTarget = this.$refs.selection;
  },
  created() {
    if (this.options !== null) this.SbSelect.hasProvidedOptions = true;
    this.SbSelect.selected = this.value;
    this.SbSelect.setSelected = this.setSelected;
  },
  methods: {
    escapeKeyHandler() {
      if (this.showDropdown) this.hideDropdownOptions();
    },
    showDropdownOptions() {
      this.showDropdown = true;
      this.setFieldState(FieldStateEnum.focus);
    },
    hideDropdownOptions() {
      this.showDropdown = false;
      if (this[this.fieldKey].state === FieldStateEnum.focus) {
        this.setFieldState(FieldStateEnum.blur);
      }
    },
    toggleDropdownOptions() {
      if (this.disabled) return;
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) this.setFieldState(FieldStateEnum.focus);
    },
    setSelected(newOption) {
      if (Array.isArray(this.selected)) this.handleArraySetSelected(newOption);
      else this.handleSingleSetSelected(newOption);
      this.$emit('update:value', this.selected);
      this.setFieldValue(this.selected);
    },
    handleArraySetSelected(newOption) {
      const arrCopy = this.selected.slice();
      if (arrCopy.includes(newOption)) {
        arrCopy.splice(arrCopy.indexOf(newOption), 1);
      } else {
        arrCopy.push(newOption);
      }
      this.selected = arrCopy;
    },
    handleSingleSetSelected(newOption) {
      this.selected = newOption;
      this.hideDropdownOptions();
    },
    getRenderOptions() {
      return this.$el?.querySelectorAll('.sb-option');
    },
    getPopOver() {
      return this.$refs['sb-options-popover'];
    },
    calculateHeight() {
      this.$nextTick(() => {
        let height = 0;
        const options = this.getRenderOptions();
        const element = this.getPopOver();
        if (options?.length > 0 && element) {
          const length = Math.min(this.resultsLimit, options.length);
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < length; i++) {
            height += options[i].offsetHeight;
          }
          element.$el.style.maxHeight = `${height}px`;
        }
      });
    },
  },
};
</script>

<style scoped lang='scss'>
@import "@src/components/Popover/style/sb-popover";

@import '@src/components/Dropdown/style/sb-dropdown.scss';
</style>
